import type { Invoice } from "@estelle/domain";

import { sumMoney } from "@/modules/gst/format";

/** One row of the HSN summary: one HSN code at one rate, for the period. */
export interface HsnSummaryRow {
  hsnCode: string;
  /** A `gstRate` is master data on the HSN code, not a per-invoice choice. */
  gstRate: string;
  quantity: number;
  taxableValue: string;
  cgstAmount: string;
  sgstAmount: string;
  igstAmount: string;
  cessAmount: string;
  total: string;
}

/**
 * Taxable value and tax, aggregated by HSN code, for the invoices passed in
 * (FR-GST-14).
 *
 * Only `issued` invoices count. A `draft` never charged tax, and a
 * `cancelled` invoice's charge was voided — counting either would overstate
 * output tax against what a return actually owes. Credit notes are not
 * netted off here: GSTR-1 reports them in their own table, so this is the
 * outward-supply side only.
 *
 * Grouped by `hsnCode` and `gstRate` together rather than `hsnCode` alone.
 * In this dataset the two never disagree — an HSN code is master data with
 * one rate — but a summary that silently merged rows under two different
 * rates into one HSN line would be wrong the one time master data drifted,
 * and splitting on both costs nothing when they always match.
 *
 * The caller decides the period by which invoices it passes in; this
 * function only groups and sums.
 */
export function aggregateHsnSummary(invoices: Invoice[]): HsnSummaryRow[] {
  interface Accumulator {
    hsnCode: string;
    gstRate: string;
    quantity: number;
    taxableValue: string[];
    cgstAmount: string[];
    sgstAmount: string[];
    igstAmount: string[];
    cessAmount: string[];
    total: string[];
  }

  const groups = new Map<string, Accumulator>();

  for (const invoice of invoices) {
    if (invoice.status !== "issued") continue;

    for (const line of invoice.lines) {
      const key = `${line.hsnCode} ${line.gstRate}`;
      let group = groups.get(key);
      if (!group) {
        group = {
          hsnCode: line.hsnCode,
          gstRate: line.gstRate,
          quantity: 0,
          taxableValue: [],
          cgstAmount: [],
          sgstAmount: [],
          igstAmount: [],
          cessAmount: [],
          total: [],
        };
        groups.set(key, group);
      }

      group.quantity += line.quantity;
      group.taxableValue.push(line.taxableValue);
      group.cgstAmount.push(line.cgstAmount);
      group.sgstAmount.push(line.sgstAmount);
      group.igstAmount.push(line.igstAmount);
      group.cessAmount.push(line.cessAmount);
      group.total.push(line.lineTotal);
    }
  }

  return Array.from(groups.values())
    .map((group) => ({
      hsnCode: group.hsnCode,
      gstRate: group.gstRate,
      quantity: group.quantity,
      taxableValue: sumMoney(group.taxableValue),
      cgstAmount: sumMoney(group.cgstAmount),
      sgstAmount: sumMoney(group.sgstAmount),
      igstAmount: sumMoney(group.igstAmount),
      cessAmount: sumMoney(group.cessAmount),
      total: sumMoney(group.total),
    }))
    .sort((a, b) => a.hsnCode.localeCompare(b.hsnCode));
}
